import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import { useIsMounted } from '@wix/da-hooks/pkg/useIsMounted';
import IconButton from '@wix/da-ds/pkg/Buttons/IconButton/IconButton';
import Close from '@wix/da-ds/pkg/Icons/16x16/Close';
import { yieldToMainAsync } from '@wix/da-hooks/pkg/yieldToMain';
import DealerAd from '../..';
import { useCloseTracking } from '../../useCloseTracking';

import s from './Banner.scss';

export interface Props {
  realEstateId: string;
  width: string;
  height: string;
  className?: string;
  withCloseButton?: boolean;
}

const BannerAdUnit: React.FC<Props> = ({
  realEstateId,
  width,
  height,
  className,
  withCloseButton = false,
}) => {
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const [passedOffer, setPassedOffer] = useState<any>(undefined);
  const [isVisible, setIsVisible] = useState(!withCloseButton);

  const iframeCommandHandler = useCallback((command, data) => {
    switch (command) {
      case 'ad-filled':
        setPassedOffer(data);
        setIsVisible(true);
        break;

      default:
        break;
    }
  }, []);

  const closeHandler = useCloseTracking(passedOffer, realEstateId, {
    iframeCommandHandler,
    onCloseStart: async () => {
      await yieldToMainAsync();
      setIsVisible(false);
    },
  });

  if (!isMounted) {
    return null;
  }

  return (
    <ErrorBoundary debugComponent="BannerAdUnit">
      <div
        className={cn(s['wrapper'], isVisible && s['visible'], className)}
        style={{ width, height }}
      >
        {withCloseButton && (
          <IconButton
            icon={Close}
            size="small"
            aria-label={t('common.hideAdvertisement')}
            onClick={closeHandler}
            className={s['close-button']}
          />
        )}

        <DealerAd
          realEstateId={realEstateId}
          extraParams={
            process.env.NODE_ENV === 'development'
              ? {
                  forcefixture: `complexbanner${width}-${height}`,
                }
              : {}
          }
        />
      </div>
    </ErrorBoundary>
  );
};
BannerAdUnit.displayName = 'BannerAdUnit';

export default BannerAdUnit;
